
import Vue from "vue";
import { ExtraFieldNamesConfig, ExtraFieldType } from "@/models/dossier-configs/extra-field-names-config";
import { ExtraFieldBaseConfig } from "@/models/dossier-configs/extra-field-base-config";
import { ExtraFieldsTextInputType, ExtraFieldTextConfig } from "@/models/dossier-configs/extra-field-text-config";
import { ExtraFieldNumberConfig } from "@/models/dossier-configs/extra-field-number-config";
import { ExtraFieldBooleanConfig } from "@/models/dossier-configs/extra-field-boolean-config";
import { ExtraFieldTimeConfig } from "@/models/dossier-configs/extra-field-time-config";
import { ExtraFieldsType } from "@/utils/models/extra-fields";
import { printHumanFloat } from "@/utils/pretty-print";
import AppExtraFieldTextOptions from "./ExtraFieldTextOptions.vue";
import AppExtraFieldNumberOptions from "./ExtraFieldNumberOptions.vue";
import AppExtraFieldBoolOptions from "./ExtraFieldBoolOptions.vue";
import AppExtraFieldTimeOptions from "./ExtraFieldTimeOptions.vue";

interface ISummaryPart {
	text:string;
	warn?:boolean;
}

const KEY_PREFIX:{[type:string]:string} = {Text: "T", Num: "N", Bool: "B", Time: "D"};

export default Vue.extend({
	data(){
		return {
			modalOpen: false,
			focused: false
		};
	},
	props: {
		settings: {
			type: Object as () => ExtraFieldNamesConfig,
			required: true
		},
		type: {
			type: String as () => ExtraFieldType,
			required: true
		},
		index: {
			type: Number,
			required: true
		},
		extraFieldsType: {
			type: String as () => ExtraFieldsType,
			required: true
		},
		autofocus: Boolean
	},
	computed: {
		fieldKey():string {
			return `${this.type}${this.index}`;
		},
		keyLabel():string {
			return `${KEY_PREFIX[this.type]}${this.index}`;
		},
		name():string {
			return (this.settings as any)[this.fieldKey] || "";
		},
		config():ExtraFieldBaseConfig {
			return (this.settings as any)[`${this.fieldKey}Config`];
		},
		optionsComponent():any {
			if (this.type == "Text") return AppExtraFieldTextOptions;
			if (this.type == "Num") return AppExtraFieldNumberOptions;
			if (this.type == "Bool") return AppExtraFieldBoolOptions;
			return AppExtraFieldTimeOptions;
		},
		summary():ISummaryPart[] {
			if (!this.name) return [{text: this.$ct("summary.options-after-naming").toString()}];
			let parts:ISummaryPart[] = this.typeSummary();
			if (this.config.HideInEditAndInfo) {
				parts.push({text: this.$ct("summary.not-in-detail").toString(), warn: true});
			}
			let tab = this.config.TabID ? this.settings.getTab(this.config.TabID) : undefined;
			if (tab) {
				parts.push({text: this.$ct("summary.tab", {name: tab.Name || this.$ct("extra-field-tab-unnamed")}).toString()});
			}
			if (this.config.EditableViaBrowseMenu) {
				parts.push({text: this.$ct("summary.via-menu").toString()});
			}
			return parts;
		},
		summaryTitle():string {
			return this.summary.map(p=>p.text).join(" · ");
		}
	},
	methods: {
		typeSummary():ISummaryPart[] {
			if (this.type == "Text") return this.textSummary(this.config as ExtraFieldTextConfig);
			if (this.type == "Num") return this.numberSummary(this.config as ExtraFieldNumberConfig);
			if (this.type == "Bool") return this.boolSummary(this.config as ExtraFieldBooleanConfig);
			return this.timeSummary(this.config as ExtraFieldTimeConfig);
		},
		textSummary(conf:ExtraFieldTextConfig):ISummaryPart[] {
			let parts:ISummaryPart[] = [];
			if (conf.InputType == ExtraFieldsTextInputType.DEFAULT) {
				parts.push({text: this.$ct("summary.free-text").toString()});
				if (conf.WithFormatting) parts.push({text: this.$ct("summary.with-formatting").toString()});
			} else {
				let key = Object.keys(ExtraFieldsTextInputType).find(k=>(ExtraFieldsTextInputType as any)[k] == conf.InputType) || "DEFAULT";
				parts.push({text: this.$ct(`extra-fields-input-types.${key}`).toString()});
				if (conf.InputType == ExtraFieldsTextInputType.SELECT || conf.InputType == ExtraFieldsTextInputType.MULTI_SELECT) {
					let count = (conf.InputMetaData || "").split("\n").filter(l=>l.trim() != "").length;
					parts.push({text: this.$ctc("summary.option-count", count, {count}).toString()});
				}
			}
			if (conf.DefaultValue) parts.push({text: this.$ct("summary.default", {value: conf.DefaultValue}).toString()});
			return parts;
		},
		numberSummary(conf:ExtraFieldNumberConfig):ISummaryPart[] {
			let parts:ISummaryPart[] = [{text: this.$ctc("summary.decimals", conf.Decimals, {count: conf.Decimals}).toString()}];
			parts.push({text: this.$ct("summary.default", {value: printHumanFloat(conf.DefaultValue || 0, conf.Decimals)}).toString()});
			return parts;
		},
		boolSummary(conf:ExtraFieldBooleanConfig):ISummaryPart[] {
			let value = conf.DefaultValue ? this.$t("common.yes") : this.$t("common.no");
			return [{text: this.$ct("summary.default", {value}).toString()}];
		},
		timeSummary(conf:ExtraFieldTimeConfig):ISummaryPart[] {
			return [{text: this.$ct("summary.format", {format: conf.DocumentFormat || "02/01/2006"}).toString()}];
		},
		onName(value:string){
			(this.settings as any)[this.fieldKey] = value;
		},
		onOptions(value:ExtraFieldBaseConfig){
			(this.settings as any)[`${this.fieldKey}Config`] = value;
		},
		focus(){
			let input = this.$refs.name as HTMLInputElement | undefined;
			if (input) input.focus();
		}
	},
	mounted(){
		if (this.autofocus) {
			this.$nextTick(()=>this.focus());
		}
	},
	i18nEx: {
		componentPrefix: "components.settings-default"
	}
});
